import React, { useState, useMemo } from 'react'
import { RejectedCoreTeam, RejectedCoreTeamTableMetaProps } from '../data/types'

type Props = {
    meta: RejectedCoreTeamTableMetaProps
    data: RejectedCoreTeam[]
}

export const RejectedCoreTeamTable = (props: Props) => {
    const { meta, data } = props
    const [sortColumn, setSortColumn] = useState<string>('created_at')
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')
    const [searchTerm, setSearchTerm] = useState('')

    const handleSort = (column: string) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
        } else {
            setSortColumn(column)
            setSortDirection('asc')
        }
    }

    const sortedAndFilteredData = useMemo(() => {
        let filtered = data

        // Apply search filter
        if (searchTerm) {
            const term = searchTerm.toLowerCase()
            filtered = filtered.filter(item => {
                const fullName = `${item.volunteer?.first_name} ${item.volunteer?.last_name}`.toLowerCase()
                const role = item.role?.toLowerCase() || ''
                const streams = item.streams?.join(' ').toLowerCase() || ''
                const rejectedBy = `${item.rejected_by?.first_name} ${item.rejected_by?.last_name}`.toLowerCase()

                return fullName.includes(term) ||
                       role.includes(term) ||
                       streams.includes(term) ||
                       rejectedBy.includes(term)
            })
        }

        // Apply sorting
        const sorted = [...filtered].sort((a, b) => {
            let aValue: any
            let bValue: any

            switch (sortColumn) {
                case 'volunteer.last_name':
                    aValue = a.volunteer?.last_name || ''
                    bValue = b.volunteer?.last_name || ''
                    break
                case 'role':
                    aValue = a.role || ''
                    bValue = b.role || ''
                    break
                case 'tier':
                    aValue = a.tier || 0
                    bValue = b.tier || 0
                    break
                case 'rejected_by.last_name':
                    aValue = a.rejected_by?.last_name || ''
                    bValue = b.rejected_by?.last_name || ''
                    break
                case 'created_at':
                    aValue = new Date(a.created_at).getTime()
                    bValue = new Date(b.created_at).getTime()
                    break
                default:
                    return 0
            }

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return sortDirection === 'asc'
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue)
            }

            if (sortDirection === 'asc') {
                return aValue > bValue ? 1 : aValue < bValue ? -1 : 0
            } else {
                return aValue < bValue ? 1 : aValue > bValue ? -1 : 0
            }
        })

        return sorted
    }, [data, sortColumn, sortDirection, searchTerm])

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        })
    }

    const renderSortIcon = (column: string) => {
        if (sortColumn !== column) return null
        return sortDirection === 'asc' ? ' ↑' : ' ↓'
    }

    if (sortedAndFilteredData.length === 0) {
        return (
            <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                <p className="text-gray-500">{meta.noDataMessage}</p>
            </div>
        )
    }

    return (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {/* Search Bar */}
            <div className="p-4 border-b border-gray-200">
                <input
                    type="text"
                    placeholder={meta.searchPlaceholder}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                            <th
                                className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSort('volunteer.last_name')}
                            >
                                {meta.nameColumn}{renderSortIcon('volunteer.last_name')}
                            </th>
                            <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {meta.streamsColumn}
                            </th>
                            <th
                                className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSort('role')}
                            >
                                {meta.roleColumn}{renderSortIcon('role')}
                            </th>
                            <th
                                className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSort('tier')}
                            >
                                {meta.tierColumn}{renderSortIcon('tier')}
                            </th>
                            <th
                                className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSort('rejected_by.last_name')}
                            >
                                {meta.rejectedByColumn}{renderSortIcon('rejected_by.last_name')}
                            </th>
                            <th
                                className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSort('created_at')}
                            >
                                {meta.rejectedDateColumn}{renderSortIcon('created_at')}
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {sortedAndFilteredData.map((item) => (
                            <tr key={item.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900">
                                        {item.volunteer?.first_name} {item.volunteer?.last_name}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                        ID: {item.volunteer?.csid}
                                    </div>
                                </td>
                                <td className="px-6 py-4">
                                    <div className="flex flex-wrap gap-1">
                                        {item.streams?.map((stream, idx) => (
                                            <span
                                                key={idx}
                                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800"
                                            >
                                                {stream}
                                            </span>
                                        ))}
                                    </div>
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-900">
                                    {item.role}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-900">
                                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-800 font-semibold">
                                        {item.tier === 5 ? 'R' : item.tier}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">
                                        {item.rejected_by?.first_name} {item.rejected_by?.last_name}
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {formatDate(item.created_at)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    )
}
